import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { GarageServiceOfferingResponse } from '../types';
import { OfferingV2Response } from '../types/offeringsV2';
import AddServiceModalV2 from '../components/services/AddServiceModalV2';
import OverviewServiceTexts from '../components/services/OverviewServiceTexts';
import PricingMatrixTab from '../components/services/PricingMatrixTab';
import HistoryTab from '../components/services/HistoryTab';
import OrderCell from '../components/services/OrderCell';

type FilterChip = 'all' | 'pending' | 'active' | 'inactive';
type WorkspaceTab = 'overview' | 'variations' | 'pricing' | 'history';

function offeringStatusLabel(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'Active';
    case 'INACTIVE': return 'Inactive';
    case 'PENDING_APPROVAL': return 'Pending review';
    case 'REJECTED': return 'Rejected';
    default: return status;
  }
}

function offeringStatusClass(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'status-badge--approved';
    case 'INACTIVE': return 'status-badge--draft';
    case 'PENDING_APPROVAL': return 'status-badge--pending';
    case 'REJECTED': return 'status-badge--rejected';
    default: return 'status-badge--draft';
  }
}

function pendingVariantCount(offering: OfferingV2Response): number {
  return offering.variants?.filter((v) => v.status === 'PENDING_APPROVAL').length ?? 0;
}

function needsChangesCount(offering: OfferingV2Response): number {
  return offering.variants?.filter((v) => v.status === 'NEEDS_CHANGES').length ?? 0;
}

export default function GarageServicesWorkspacePage() {
  const { garageId } = useParams<{ garageId: string }>();
  const auth = useAuth();

  const [offerings, setOfferings] = useState<GarageServiceOfferingResponse[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedOffering, setSelectedOffering] = useState<OfferingV2Response | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // The Service Approval list links here with ?filter=pending (#908 item 2).
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<FilterChip>(searchParams.get('filter') === 'pending' ? 'pending' : 'all');
  const [search, setSearch] = useState('');

  const [activeTab, setActiveTab] = useState<WorkspaceTab>('overview');

  const [showWizard, setShowWizard] = useState(false);
  const [editOfferingId, setEditOfferingId] = useState<string | undefined>(undefined);

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const [garageName, setGarageName] = useState<string>('');
  const [reordering, setReordering] = useState(false);

  // The token string changes on every refresh (~14 min). Keying the loads below on it reloaded
  // the list and detail behind the admin — spinner, tab reset — mid-review (#908 item 5).
  // They re-run only when a token first appears; each call reads the current one.
  const hasToken = !!auth.token;

  // Load garage name
  useEffect(() => {
    if (!auth.token || !garageId) return;
    api.getGarageById(garageId, auth.token)
      .then((g) => setGarageName(g.name ?? garageId))
      .catch(() => setGarageName(garageId!));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garageId, hasToken]);

  // Load offerings list
  function loadOfferings() {
    if (!auth.token || !garageId) return;
    setLoadingList(true);
    setListError(null);
    api.getGarageServices(garageId, auth.token)
      .then(setOfferings)
      .catch((err) => setListError(err.message))
      .finally(() => setLoadingList(false));
  }

  useEffect(() => {
    loadOfferings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garageId, hasToken]);

  // Load V2 detail when selected
  useEffect(() => {
    if (!selectedId || !auth.token || !garageId) {
      setSelectedOffering(null);
      return;
    }
    setLoadingDetail(true);
    setDetailError(null);
    setActionError(null);
    setShowRejectInput(false);
    api.getAdminOfferingV2(garageId, selectedId, auth.token)
      .then((o) => {
        setSelectedOffering(o);
        // Land on Pricing tab if anything awaits review, otherwise Overview
        setActiveTab(o.status === 'PENDING_APPROVAL' || pendingVariantCount(o) > 0 ? 'pricing' : 'overview');
      })
      .catch((err) => setDetailError(err.message))
      .finally(() => setLoadingDetail(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, garageId, hasToken]);

  // The garage's services in their saved display order — what a customer sees on the garage
  // profile. The API returns them ordered; sorting again keeps the arrows honest.
  const orderedOfferings = useMemo(
    () => [...offerings].sort((a, b) => {
      const ao = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const bo = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      // createdAt only breaks ties among offerings that predate the display_order
      // backfill. It is typed as required but can be absent in practice, and this
      // comparator runs on every render — an undefined here takes the page down.
      return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
    }),
    [offerings]
  );

  // Reordering rewrites every position, so it only means something while the list shows the
  // real order — under a filter or search the row above is not the true neighbour.
  const canReorder = !search.trim() && filter === 'all';

  function moveOfferingTo(offeringId: string, targetPosition: number) {
    if (!auth.token || !garageId) return;
    const current = orderedOfferings.map((o) => o.id);
    const from = current.indexOf(offeringId);
    const to = Math.min(Math.max(targetPosition, 1), current.length) - 1;
    if (from < 0 || from === to) return;

    const next = [...current];
    next.splice(to, 0, next.splice(from, 1)[0]);

    const previous = offerings;
    const byId = new Map(offerings.map((o) => [o.id, o]));
    // Optimistic, so repeated arrow clicks don't fight the round trip.
    setOfferings(next.map((id, i) => ({ ...byId.get(id)!, displayOrder: i + 1 })));
    setReordering(true);
    api.adminReorderOfferings(garageId, next, auth.token)
      .then(setOfferings)
      .catch((err) => {
        setOfferings(previous); // saved order is unchanged — put the list back
        setListError(err.message);
      })
      .finally(() => setReordering(false));
  }

  // Filtered + searched list
  const filteredOfferings = useMemo(() => {
    let list = orderedOfferings;
    if (filter === 'pending') list = list.filter((o) => o.status === 'PENDING_APPROVAL' || (o.pendingVariantCount ?? 0) > 0);
    else if (filter === 'active') list = list.filter((o) => o.status === 'ACTIVE');
    else if (filter === 'inactive') list = list.filter((o) => o.status === 'INACTIVE');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((o) => (o.name ?? '').toLowerCase().includes(q));
    }
    return list;
  }, [orderedOfferings, filter, search]);

  function handleSelectOffering(id: string) {
    if (id === selectedId) return;
    setSelectedId(id);
  }

  function handleApprove() {
    if (!selectedId || !auth.token) return;
    setActionLoading(true);
    setActionError(null);
    api.approveOffering(selectedId, auth.token)
      .then(() => {
        loadOfferings();
        // Reload detail
        setSelectedId((prev) => {
          // force re-fetch by briefly clearing
          setTimeout(() => setSelectedId(prev), 0);
          return null;
        });
      })
      .catch((err) => setActionError(err.message))
      .finally(() => setActionLoading(false));
  }

  function handleReject() {
    if (!selectedId || !auth.token) return;
    if (!rejectReason.trim()) {
      setShowRejectInput(true);
      return;
    }
    setActionLoading(true);
    setActionError(null);
    api.rejectOffering(selectedId, rejectReason, auth.token)
      .then(() => {
        loadOfferings();
        setSelectedId(null);
        setSelectedOffering(null);
        setRejectReason('');
        setShowRejectInput(false);
      })
      .catch((err) => setActionError(err.message))
      .finally(() => setActionLoading(false));
  }

  function handleOpenWizardCreate() {
    setEditOfferingId(undefined);
    setShowWizard(true);
  }

  function handleOpenWizardEdit() {
    if (!selectedId) return;
    setEditOfferingId(selectedId);
    setShowWizard(true);
  }

  function handleWizardSaved() {
    setShowWizard(false);
    loadOfferings();
    if (editOfferingId) {
      // Re-select the edited offering to reload its detail
      const prevId = editOfferingId;
      setSelectedId(null);
      setTimeout(() => setSelectedId(prevId), 0);
    }
  }

  const pendingCount = selectedOffering ? pendingVariantCount(selectedOffering) : 0;
  const changesCount = selectedOffering ? needsChangesCount(selectedOffering) : 0;

  return (
    <div className="workspace-page">
      {/* Left Rail */}
      <div className="workspace-left">
        <div className="workspace-left-header">
          <div className="workspace-garage-name" title={garageName}>
            {garageName || garageId}
          </div>
          <div className="workspace-service-count">
            {offerings.length} service{offerings.length !== 1 ? 's' : ''}
          </div>
        </div>

        <button
          className="admin-btn admin-btn-primary workspace-new-btn"
          onClick={handleOpenWizardCreate}
        >
          + New Service
        </button>

        <input
          className="workspace-search"
          type="text"
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="workspace-filter-chips">
          {(['all', 'pending', 'active', 'inactive'] as FilterChip[]).map((chip) => (
            <button
              key={chip}
              className={`workspace-chip${filter === chip ? ' workspace-chip--active' : ''}`}
              onClick={() => setFilter(chip)}
            >
              {chip === 'all' ? 'All' : chip === 'pending' ? 'Pending review' : chip === 'active' ? 'Active' : 'Inactive'}
            </button>
          ))}
        </div>

        {listError && (
          <div className="admin-info-banner danger" style={{ margin: '8px 0', fontSize: '13px' }}>
            {listError}
          </div>
        )}

        {loadingList ? (
          <div className="admin-loading" style={{ paddingTop: '24px' }}>
            <div className="admin-loading-spinner"></div>
          </div>
        ) : filteredOfferings.length === 0 ? (
          <div className="workspace-empty-list">
            {search || filter !== 'all' ? 'No services match the filter.' : 'No services yet.'}
          </div>
        ) : (
          <div className="workspace-offering-list">
            {filteredOfferings.map((o, rowIndex) => (
              <div
                key={o.id}
                role="button"
                tabIndex={0}
                className={`workspace-offering-row${selectedId === o.id ? ' workspace-offering-row--selected' : ''}`}
                onClick={() => handleSelectOffering(o.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectOffering(o.id);
                  }
                }}
              >
                <div className="workspace-offering-row-main">
                  <div className="workspace-offering-name">{o.name ?? '(Unnamed)'}</div>
                  <div className="workspace-offering-meta">
                    <span className={`status-badge ${offeringStatusClass(o.status)}`}>
                      {offeringStatusLabel(o.status)}
                    </span>
                    {(o.pendingVariantCount ?? 0) > 0 && (
                      <span className="workspace-counter-pill workspace-counter-pill--pending">
                        {o.pendingVariantCount} pending
                      </span>
                    )}
                  </div>
                </div>
                <OrderCell
                  position={o.displayOrder ?? (rowIndex + 1)}
                  total={orderedOfferings.length}
                  disabled={!canReorder || reordering}
                  disabledHint="Clear the search and filter to reorder this garage's services"
                  onMove={(to) => moveOfferingTo(o.id, to)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right Pane */}
      <div className="workspace-right">
        {!selectedId ? (
          <div className="workspace-empty">
            <p>Select a service to review, or click <strong>+ New Service</strong> to create one.</p>
          </div>
        ) : loadingDetail ? (
          <div className="admin-loading" style={{ paddingTop: '48px' }}>
            <div className="admin-loading-spinner"></div>
          </div>
        ) : detailError ? (
          <div className="admin-info-banner danger">{detailError}</div>
        ) : selectedOffering ? (
          <div className="workspace-detail">
            {/* Header */}
            <div className="workspace-header">
              <div className="workspace-header-left">
                <h2 className="workspace-offering-title">{selectedOffering.name}</h2>
                <span className={`status-badge ${offeringStatusClass(selectedOffering.status)}`}>
                  {offeringStatusLabel(selectedOffering.status)}
                </span>
                {pendingCount > 0 && (
                  <span className="workspace-counter-pill workspace-counter-pill--pending">
                    {pendingCount} pending
                  </span>
                )}
                {changesCount > 0 && (
                  <span className="workspace-counter-pill workspace-counter-pill--changes">
                    {changesCount} changes
                  </span>
                )}
              </div>
              <div className="workspace-header-actions">
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  onClick={handleOpenWizardEdit}
                >
                  Edit
                </button>
                {selectedOffering.status === 'PENDING_APPROVAL' && (
                  <>
                    <button
                      className="admin-btn admin-btn-success admin-btn-sm"
                      onClick={handleApprove}
                      disabled={actionLoading}
                    >
                      Approve
                    </button>
                    <button
                      className="admin-btn admin-btn-danger admin-btn-sm"
                      onClick={() => setShowRejectInput(true)}
                      disabled={actionLoading}
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>

            {actionError && (
              <div className="admin-info-banner danger" style={{ margin: '8px 0' }}>
                {actionError}
              </div>
            )}

            {showRejectInput && (
              <div className="workspace-reject-panel">
                <label className="workspace-reject-label">Rejection reason</label>
                <textarea
                  className="workspace-reject-textarea"
                  placeholder="Explain why this offering is rejected..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                />
                <div className="workspace-reject-actions">
                  <button
                    className="admin-btn admin-btn-danger admin-btn-sm"
                    onClick={handleReject}
                    disabled={actionLoading || !rejectReason.trim()}
                  >
                    Confirm Reject
                  </button>
                  <button
                    className="admin-btn admin-btn-outline admin-btn-sm"
                    onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="workspace-tabs">
              {(['overview', 'variations', 'pricing', 'history'] as WorkspaceTab[]).map((tab) => (
                <button
                  key={tab}
                  className={`workspace-tab${activeTab === tab ? ' workspace-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="workspace-tab-content">
              {activeTab === 'overview' && (
                <OverviewTab offering={selectedOffering} garageId={garageId!} token={auth.token!} />
              )}
              {activeTab === 'variations' && (
                <VariationsTab offering={selectedOffering} />
              )}
              {activeTab === 'pricing' && (
                <PricingMatrixTab
                  offering={selectedOffering}
                  token={auth.token!}
                  onChanged={async () => {
                    loadOfferings();
                    const prevId = selectedId;
                    setSelectedId(null);
                    setTimeout(() => setSelectedId(prevId), 0);
                  }}
                />
              )}
              {activeTab === 'history' && (
                <HistoryTab offeringId={selectedOffering.id} token={auth.token!} />
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Wizard modal */}
      {showWizard && garageId && auth.token && (
        <AddServiceModalV2
          garageId={garageId}
          token={auth.token}
          onClose={() => setShowWizard(false)}
          onSaved={handleWizardSaved}
          editOfferingId={editOfferingId}
        />
      )}
    </div>
  );
}

// ---- Overview Tab ----

interface OverviewTabProps {
  offering: OfferingV2Response;
  garageId: string;
  token: string;
}

function OverviewTab({ offering }: OverviewTabProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  return (
    <div className="workspace-overview">
      <div className="workspace-overview-grid">
        {offering.categoryName && (
          <div className="workspace-overview-row">
            <span className="workspace-overview-label">Category</span>
            <span className="workspace-overview-value">{offering.categoryName}</span>
          </div>
        )}
        {offering.subcategoryName && (
          <div className="workspace-overview-row">
            <span className="workspace-overview-label">Subcategory</span>
            <span className="workspace-overview-value">{offering.subcategoryName}</span>
          </div>
        )}
        {offering.description && (
          <div className="workspace-overview-row">
            <span className="workspace-overview-label">Description</span>
            <span className="workspace-overview-value">{offering.description}</span>
          </div>
        )}
        {offering.warrantyDescription && (
          <div className="workspace-overview-row">
            <span className="workspace-overview-label">Warranty</span>
            <span className="workspace-overview-value">{offering.warrantyDescription}</span>
          </div>
        )}
        <OverviewServiceTexts offering={offering} />
        {offering.isCarSpecific === false && offering.flatPrice != null && (
          <div className="workspace-overview-row">
            <span className="workspace-overview-label">Flat price</span>
            <span className="workspace-overview-value">
              AED {offering.flatPrice}
              {offering.flatDurationMinutes != null && ` • ${offering.flatDurationMinutes} min`}
            </span>
          </div>
        )}
        {offering.rejectionReason && (
          <div className="workspace-overview-row">
            <span className="workspace-overview-label">Rejection reason</span>
            <span className="workspace-overview-value workspace-overview-value--danger">
              {offering.rejectionReason}
            </span>
          </div>
        )}
      </div>

      {offering.thumbnailUrl && (
        <div className="workspace-media-section">
          <div className="workspace-media-label">Thumbnail</div>
          <img
            className="workspace-thumbnail"
            src={offering.thumbnailUrl}
            alt="Thumbnail"
            onClick={() => setLightboxSrc(offering.thumbnailUrl!)}
          />
        </div>
      )}

      {offering.photoUrls && offering.photoUrls.length > 0 && (
        <div className="workspace-media-section">
          <div className="workspace-media-label">Photos</div>
          <div className="workspace-photo-gallery">
            {offering.photoUrls.map((url, i) => (
              <img
                key={i}
                className="workspace-gallery-thumb"
                src={url}
                alt={`Photo ${i + 1}`}
                onClick={() => setLightboxSrc(url)}
              />
            ))}
          </div>
        </div>
      )}

      {lightboxSrc && (
        <div className="workspace-lightbox" onClick={() => setLightboxSrc(null)}>
          <img src={lightboxSrc} alt="Enlarged" className="workspace-lightbox-img" />
        </div>
      )}
    </div>
  );
}

// ---- Variations Tab ----

interface VariationsTabProps {
  offering: OfferingV2Response;
}

function VariationsTab({ offering }: VariationsTabProps) {
  if (!offering.variations || offering.variations.length === 0) {
    return (
      <div className="workspace-variations">
        <div className="workspace-empty-list">No variations defined for this service.</div>
      </div>
    );
  }

  return (
    <div className="workspace-variations">
      {offering.variations.map((variation) => (
        <div key={variation.id} className="workspace-variation-group">
          <div className="workspace-variation-name">
            {variation.name}
            {variation.selectionMode === 'MULTI' && (
              <span className="workspace-counter-pill" style={{ marginLeft: '8px' }}>Multi-select</span>
            )}
          </div>
          <div className="workspace-variation-values">
            {variation.values.map((val) => (
              <span key={val.id} className="workspace-variation-value-chip">
                {val.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
